// src/features/auth/services/authService.ts
import axios from 'axios';
import apiClient from '../../../services/apiClient'; // Use shared API client

// Create a separate client for auth endpoints without credentials to avoid CSRF issues
const authApiClient = axios.create({
  baseURL: apiClient.defaults.baseURL,
  withCredentials: false, // No cookies for auth endpoints
  headers: {
    'Content-Type': 'application/json'
  }
});
import { preloadService } from '../../../services/preloadService';
import { 
  LoginRequest, 
  LoginResponse, 
  RegisterRequest, 
  RegisterResponse, 
  UserPasswordUpdateRequest,
  TwoFactorSetupResponse,
  TwoFactorVerifyRequest,
  TwoFactorStatusResponse,
  TwoFactorDisableRequest,
  TwoFactorRegenerateBackupCodesRequest,
  TwoFactorRegenerateBackupCodesResponse,
  PasswordStrengthRequest,
  PasswordStrengthResponse,
  PasswordPolicyResponse,
  PasswordResetRequest,
  PasswordResetVerifyRequest,
  PasswordResetConfirmRequest,
  EnhancedLogoutRequest,
  EnhancedLogoutResponse,
  UserSessionsResponse,
  SessionManageRequest,
  SessionManageResponse,
  SessionDetailResponse,
  TokenRefreshRequest,
  TokenRefreshResponse,
  UserInfoResponse,
  RateLimitError,
  RateLimitStatusResponse,
  BlacklistedTokensResponse,
  AuthEventsResponse,
  AuthEventTypesResponse,
  UserAuthEventsResponse,
  PasswordChangeRequest,
  TwoFactorEnableRequest,
  TwoFactorEnableResponse,
  OpenAPILoginResponse,
  AppUser,
  UserProfileUpdateRequest,
  UserProfileUpdateResponse
} from '../types';

// Response interceptor to handle rate limiting only (removed automatic token refresh to prevent loops)
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Handle rate limiting
    if (error.response?.status === 429) {
      const rateLimitError: RateLimitError = {
        error: error.response.data.error || 'Too many requests',
        retry_after: parseInt(error.response.headers['retry-after'] || '60'),
        limit: parseInt(error.response.headers['x-ratelimit-limit'] || '0'),
        remaining: parseInt(error.response.headers['x-ratelimit-remaining'] || '0')
      };
      error.rateLimitInfo = rateLimitError;
    }

    throw error;
  }
);

// Timer for token refresh
const tokenRefreshTimer: ReturnType<typeof setTimeout> | null = null;

export const authService = {
  // Reference to token refresh timer
  tokenRefreshTimer: null as ReturnType<typeof setTimeout> | null,
  // Registration
  register: async (data: RegisterRequest): Promise<RegisterResponse> => {
    try {
      const response = await authApiClient.post<RegisterResponse>('/api/auth/register/', data);
      
      // Store tokens after successful registration
      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token || '');
        if (response.data.access_expires_in) {
          localStorage.setItem('token_expires_at', 
            (Date.now() + response.data.access_expires_in * 1000).toString()
          );
        }
      }
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // Login with 2FA support
  login: async (data: LoginRequest): Promise<LoginResponse> => {
    try {
      // Clear any existing tokens before attempting new login
      authService.clearTokens();
      
      const response = await authApiClient.post<LoginResponse>('/api/auth/login/', data);
      
      // Check if 2FA is required
      if (response.data.temp_token) {
        // 2FA challenge - don't store tokens yet
        return {
          temp_token: response.data.temp_token,
          requires_2fa: true
        };
      }
      
      // Normal login success - store tokens
      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token || '');
        if (response.data.access_expires_in) {
          localStorage.setItem('token_expires_at', 
            (Date.now() + response.data.access_expires_in * 1000).toString()
          );
        }
        
        // Start token refresh service after successful login
        authService.startTokenRefreshService();
      }
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // 2FA verification
  verify2FA: async (data: TwoFactorVerifyRequest): Promise<LoginResponse> => {
    try {
      const response = await apiClient.post<LoginResponse>('/api/auth/2fa/verify/', data);
      
      // Store tokens after successful 2FA verification
      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token || '');
        if (response.data.access_expires_in) {
          localStorage.setItem('token_expires_at', 
            (Date.now() + response.data.access_expires_in * 1000).toString()
          );
        }
        
        // Start token refresh service after successful 2FA verification
        authService.startTokenRefreshService();
      }
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // 2FA setup
  setup2FA: async (): Promise<TwoFactorSetupResponse> => {
    try {
      const response = await apiClient.post<TwoFactorSetupResponse>('/api/auth/2fa/setup/');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // Enable 2FA after verification
  enable2FA: async (request: TwoFactorEnableRequest): Promise<TwoFactorEnableResponse> => {
    try {
      
      
      // Get the access token directly
      const token = localStorage.getItem('access_token');
      
      
      // Create headers explicitly with Authorization
      const headers = {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'application/json'
      };
      
      
      // Make the API call with explicit headers
      const response = await apiClient.post<TwoFactorEnableResponse>('/api/auth/2fa/enable/', 
        request, 
        { headers, withCredentials: true }
      );
      
      
      
      return response.data;
    } catch (error) {
      
      if (axios.isAxiosError(error) && error.response) {
        
        throw error.response.data;
      }
      throw error;
    }
  },

  // Disable 2FA
  disable2FA: async (data: TwoFactorDisableRequest): Promise<void> => {
    try {
      await apiClient.post('/api/auth/2fa/disable/', data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // Regenerate 2FA backup codes
  regenerate2FABackupCodes: async (data: TwoFactorRegenerateBackupCodesRequest): Promise<TwoFactorRegenerateBackupCodesResponse> => {
    try {
      const response = await apiClient.post<TwoFactorRegenerateBackupCodesResponse>('/api/auth/2fa/regenerate-backup-codes/', data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // Get 2FA status
  get2FAStatus: async (): Promise<TwoFactorStatusResponse> => {
    try {
      const response = await apiClient.get<TwoFactorStatusResponse>('/api/auth/2fa/status/');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // Password strength validation
  checkPasswordStrength: async (data: PasswordStrengthRequest): Promise<PasswordStrengthResponse> => {
    try {
      const response = await apiClient.post<PasswordStrengthResponse>('/api/auth/check-password-strength/', data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // Get password policy
  getPasswordPolicy: async (): Promise<PasswordPolicyResponse> => {
    try {
      const response = await apiClient.get<PasswordPolicyResponse>('/api/auth/password-policy/');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // Password reset flow
  requestPasswordReset: async (data: PasswordResetRequest): Promise<void> => {
    try {
      await apiClient.post('/api/auth/request-password-reset/', data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  verifyResetToken: async (data: PasswordResetVerifyRequest): Promise<void> => {
    try {
      await apiClient.post('/api/auth/verify-reset-token/', data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  resetPassword: async (data: PasswordResetConfirmRequest): Promise<void> => {
    try {
      await apiClient.post('/api/auth/reset-password/', data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // Enhanced logout
  enhancedLogout: async (data: EnhancedLogoutRequest): Promise<EnhancedLogoutResponse> => {
    try {
      const response = await apiClient.post<EnhancedLogoutResponse>('/api/auth/logout-enhanced/', data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // Basic logout
  logout: async (): Promise<void> => {
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        await apiClient.post('/api/auth/logout/', {});
      }
    } catch (error) {
      
    } finally {
      // Always clear tokens and stop refresh service (cache clearing handled in clearTokens)
      authService.clearTokens();
      authService.stopTokenRefreshService();
    }
  },

  // Token refresh functionality
  refreshToken: async (): Promise<TokenRefreshResponse> => {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await apiClient.post<TokenRefreshResponse>('/api/auth/refresh-token/', {
        refresh_token: refreshToken
      });

      // Store new tokens
      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token || '');
        localStorage.setItem('token_expires_at', 
          (Date.now() + response.data.access_expires_in * 1000).toString()
        );
      }

      return response.data;
    } catch (error) {
      // If refresh fails, logout user
      authService.logout();
      throw error;
    }
  },

  // Check if token is expired or about to expire
  isTokenExpired: (bufferMinutes: number = 5): boolean => {
    const expiresAt = localStorage.getItem('token_expires_at');
    if (!expiresAt) return true;
    
    // Consider token expired if it expires within the specified buffer time (default: 5 minutes)
    return Date.now() >= (parseInt(expiresAt) - bufferMinutes * 60 * 1000);
  },

  // Get current access token, refresh if needed
  getValidToken: async (): Promise<string | null> => {
    const token = localStorage.getItem('access_token');
    if (!token) return null;

    if (authService.isTokenExpired()) {
      try {
        const refreshed = await authService.refreshToken();
        return refreshed.access_token;
      } catch (error) {
        return null;
      }
    }

    return token;
  },

  // Get current user information
  getUserInfo: async (): Promise<UserInfoResponse> => {
    try {
      const response = await apiClient.get('/api/auth/user-info/');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // Update user profile
  updateProfile: async (data: UserProfileUpdateRequest): Promise<UserProfileUpdateResponse> => {
    try {
      const response = await apiClient.put<UserProfileUpdateResponse>('/api/auth/user-profile/', data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // Get user sessions
  getUserSessions: async (): Promise<UserSessionsResponse> => {
    try {
      const response = await apiClient.get<UserSessionsResponse>('/api/auth/sessions/');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // Manage user sessions
  manageUserSession: async (data: SessionManageRequest): Promise<SessionManageResponse> => {
    try {
      const response = await apiClient.post<SessionManageResponse>('/api/auth/sessions/manage/', data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // Get session details
  getSessionDetails: async (sessionId: string): Promise<SessionDetailResponse> => {
    try {
      const response = await apiClient.get<SessionDetailResponse>(`/auth/sessions/${sessionId}/`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // Change password (for authenticated users)
  changePassword: async (data: PasswordChangeRequest): Promise<void> => {
    try {
      await apiClient.post('/api/auth/change-password/', data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // Get rate limit status
  getRateLimitStatus: async (): Promise<RateLimitStatusResponse> => {
    try {
      const response = await apiClient.get<RateLimitStatusResponse>('/api/auth/rate-limit-status/');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // Get blacklisted tokens
  getBlacklistedTokens: async (): Promise<BlacklistedTokensResponse> => {
    try {
      const response = await apiClient.get<BlacklistedTokensResponse>('/api/auth/blacklisted-tokens/');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // Get auth events
  getAuthEvents: async (): Promise<AuthEventsResponse> => {
    try {
      const response = await apiClient.get<AuthEventsResponse>('/api/auth/events/');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // Get auth event types
  getAuthEventTypes: async (): Promise<AuthEventTypesResponse> => {
    try {
      const response = await apiClient.get<AuthEventTypesResponse>('/api/auth/event-types/');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // Get user auth events
  getUserAuthEvents: async (userId: string | number): Promise<UserAuthEventsResponse> => {
    try {
      const response = await apiClient.get<UserAuthEventsResponse>(`/auth/users/${userId}/events/`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error.response.data;
      }
      throw error;
    }
  },

  // Utility function to check if user is authenticated
  isAuthenticated: (): boolean => {
    // Check if we have secure cookie auth available
    if (typeof document !== 'undefined') {
      const cookies = document.cookie.split(';');
      const hasAuthCookie = cookies.some(cookie => cookie.trim().startsWith('access_token='));
      if (hasAuthCookie) {
        return true;
      }
    }
    
    // Fallback to localStorage for backward compatibility (during migration)
    const token = localStorage.getItem('access_token');
    return !!token && !authService.isTokenExpired();
  },

  // Utility function to handle rate limit errors
  handleRateLimit: (error: any): RateLimitError | null => {
    // Only return rate limit info if the error has it AND it's a 429 status
    if (error.rateLimitInfo && error.response?.status === 429) {
      return error.rateLimitInfo;
    }
    return null;
  },

  // Clear tokens from localStorage (without API call)
  clearTokens: (): void => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expires_at');
    // Clear any active token refresh timers
    if (authService.tokenRefreshTimer) {
      clearTimeout(authService.tokenRefreshTimer);
      authService.tokenRefreshTimer = null;
    }
    // Clear preload cache when tokens are cleared (logout scenarios)
    preloadService.clearCache();
  },

  // Start proactive token refresh service
  startTokenRefreshService: (refreshIntervalMinutes: number = 10): void => {
    // Clear any existing timer first
    if (authService.tokenRefreshTimer) {
      clearTimeout(authService.tokenRefreshTimer);
      authService.tokenRefreshTimer = null;
    }
    
    // Function to check and refresh token if needed
    const checkAndRefreshToken = async () => {
      try {
        // Check if user is authenticated and token exists
        const token = localStorage.getItem('access_token');
        const refreshToken = localStorage.getItem('refresh_token');
        
        if (!token || !refreshToken) {
          return; // No tokens to refresh
        }
        
        // Check if token will expire soon (within 15 minutes)
        if (authService.isTokenExpired(15)) {
          
          await authService.refreshToken();
          
        }
        
        // Schedule next check
        authService.tokenRefreshTimer = setTimeout(
          checkAndRefreshToken, 
          refreshIntervalMinutes * 60 * 1000
        );
      } catch (error) {
        
        // Still schedule next check even if this one failed
        authService.tokenRefreshTimer = setTimeout(
          checkAndRefreshToken, 
          refreshIntervalMinutes * 60 * 1000
        );
      }
    };
    
    // Start the first check
    checkAndRefreshToken();
  },

  // Stop token refresh service
  stopTokenRefreshService: (): void => {
    if (authService.tokenRefreshTimer) {
      clearTimeout(authService.tokenRefreshTimer);
      authService.tokenRefreshTimer = null;
    }
  },

  // OpenAPI-compliant methods
  loginOpenAPI: async (data: LoginRequest): Promise<OpenAPILoginResponse> => {
    try {
      authService.clearTokens();
      const response = await apiClient.post<OpenAPILoginResponse>('/api/auth/login-csrf-free/', data);
      
      // Check if 2FA is required
      if (response.data.requires_2fa && response.data.temp_token) {
        return {
          access_token: '',
          refresh_token: '',
          user: {} as AppUser,
          requires_2fa: true,
          temp_token: response.data.temp_token
        };
      }
      
      // Store tokens after successful login
      if (response.data.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
      }
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  },

  getUserInfoOpenAPI: async (): Promise<AppUser> => {
    try {
      const response = await apiClient.get<AppUser>('/api/auth/user-info/');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  },

  setupTwoFactorOpenAPI: async (): Promise<TwoFactorSetupResponse> => {
    try {
      const response = await apiClient.post<TwoFactorSetupResponse>('/api/auth/2fa/setup/');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  },

  enableTwoFactorOpenAPI: async (data: TwoFactorEnableRequest): Promise<{ message: string }> => {
    try {
      const response = await apiClient.post<{ message: string }>('/api/auth/2fa/enable/', data);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }
};

export default authService;
